import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Navigation, Star, Globe, Crosshair, Loader2, ArrowRight, Map, BookOpen, Timer, BarChart3, Dumbbell, CheckCircle2, ChevronDown, Search as SearchIcon } from 'lucide-react';
import { ALL_GYMS, GYM_STATES, STATE_NAMES } from '../lib/hyroxGymData';
import { CITY_COORDS, getDistanceMiles } from '../lib/cityCoords';
import { articlesApi } from '../lib/api';
import { PageSEO, PageFAQSchema } from '../components/seo/SchemaMarkup';
import { HYROX_CITIES } from './hyrox/data';
import { SubmitGymForm } from './hyrox/SubmitGymForm';
import { trackGAEvent } from '../lib/analytics';

const SITE_URL = 'https://ninjathlete.com';

const NEAR_ME_FAQS = [
  { question: 'How do I find a HYROX gym near me?', answer: `Use the location finder on this page — tap "Use My Location" and we'll instantly rank all ${ALL_GYMS.length} HYROX-ready gyms in our directory by distance from you. You can also browse by state or jump into one of our 16 in-depth city gym guides covering New York, LA, Chicago, Dallas, Miami, and more.` },
  { question: 'What is the difference between an Official HYROX Partner gym and a HYROX-aligned gym?', answer: 'Official HYROX Partner gyms and Training Clubs are licensed by HYROX and run standardized HYROX programming with race-spec equipment (sleds, SkiErgs, wall ball targets). HYROX-aligned gyms are CrossFit boxes and functional fitness facilities that offer the equipment and class styles needed to prepare for a race, but without official affiliation. Both can get you race-ready — official partners are best if you want race simulations.' },
  { question: 'Can I train for HYROX at a regular gym?', answer: 'Yes — if it has a SkiErg, rower, sled track (or turf strip), wall ball space, and room to run intervals. The stations most commercial gyms lack are the sled push/pull and burpee broad jumps. If your gym is missing those, supplement with one race simulation per month at an official partner gym listed here.' },
  { question: 'How much do HYROX gym memberships cost?', answer: 'F45 and BFT HYROX Training Clubs typically run $150–$250/month for unlimited classes. Official HYROX Partner gyms in major metros (NYC, LA, Miami) range from $200–$350/month. CrossFit-style HYROX-aligned gyms average $130–$200/month. Many offer free trial classes — check the gym website links in our directory.' },
  { question: 'Are there HYROX races near me?', answer: 'HYROX runs races in 11+ US cities each season including New York, Chicago, Dallas, Anaheim, Miami, and Washington D.C. Check the official HYROX events calendar for dates, then use our free Finish Time Predictor and 8-Week Training Plan to prep for race day.' },
  { question: "What if there's no HYROX gym in my city?", answer: 'You can still train effectively. Follow our 8-week HYROX training plan using a standard gym plus running, and swap stations you lack equipment for (e.g., heavy sled push → weighted uphill treadmill walk, sled pull → heavy rope rows). Submit your local gym to our directory if it offers HYROX-style training — we review every submission.' },
];

const nearestCityLabel = (loc) => {
  let best = null;
  let bestDist = Infinity;
  Object.entries(CITY_COORDS).forEach(([city, [lat, lng]]) => {
    const d = getDistanceMiles(loc.lat, loc.lng, lat, lng);
    if (d < bestDist) { bestDist = d; best = city; }
  });
  return best;
};

const NearGymCard = ({ gym, distance }) => {
  const isOfficial = gym.tier.toLowerCase().includes('official') || gym.tier.toLowerCase().includes('performance center');
  const id = gym.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return (
    <div className="bg-zinc-900/90 border border-zinc-800 hover:border-[#e8ff00]/40 p-5 transition-all flex flex-col" data-testid={`near-gym-card-${id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="text-white font-heading text-base leading-tight">{gym.name}</h3>
        {distance !== null && distance !== undefined && (
          <span className="flex items-center gap-1 px-2 py-1 bg-[#e8ff00]/10 border border-[#e8ff00]/30 shrink-0" data-testid={`near-gym-distance-${id}`}>
            <Navigation className="w-3 h-3 text-[#e8ff00]" />
            <span className="text-[#e8ff00] text-xs font-heading">{distance} mi</span>
          </span>
        )}
      </div>
      <p className="text-zinc-500 text-xs flex items-center gap-1.5 mb-3">
        <MapPin className="w-3 h-3" /> {gym.city}, {gym.state}
        <span className="text-zinc-700">•</span>
        <Star className="w-3 h-3 text-[#e8ff00]" /> {gym.rating}
      </p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-heading ${isOfficial ? 'bg-[#e8ff00]/10 text-[#e8ff00] border border-[#e8ff00]/30' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'}`}>
          {isOfficial ? 'Official Partner' : 'HYROX-Aligned'}
        </span>
        {gym.raceSims && <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-heading bg-[#E63946]/10 text-[#E63946] border border-[#E63946]/30">Race Sims</span>}
      </div>
      <p className="text-zinc-500 text-[11px] leading-relaxed mb-4 flex-1">{gym.notableInfo}</p>
      {gym.website ? (
        <a href={gym.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-[#e8ff00] text-black text-xs font-heading uppercase tracking-wider hover:opacity-90 transition-opacity" data-testid={`near-gym-visit-${id}`}>
          Visit Website <Globe className="w-3 h-3" />
        </a>
      ) : (
        <a href={`https://www.google.com/maps/search/${encodeURIComponent(`${gym.name} ${gym.city} ${gym.state}`)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-zinc-700 text-zinc-300 text-xs font-heading uppercase tracking-wider hover:border-[#e8ff00]/40 hover:text-[#e8ff00] transition-colors">
          Find on Maps <Map className="w-3 h-3" />
        </a>
      )}
    </div>
  );
};

export default function HyroxNearMePage() {
  const [geoStatus, setGeoStatus] = useState('idle');
  const [userLocation, setUserLocation] = useState(null);
  const [selectedState, setSelectedState] = useState('');
  const [openFaq, setOpenFaq] = useState(0);
  const [searchQ, setSearchQ] = useState('');
  const [dirMatches, setDirMatches] = useState([]);

  useEffect(() => {
    if (!searchQ || searchQ.trim().length < 2) { setDirMatches([]); return undefined; }
    const t = setTimeout(() => {
      articlesApi.siteSearch(searchQ.trim())
        .then(({ data }) => setDirMatches(data.gyms || []))
        .catch(() => setDirMatches([]));
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // ItemList JSON-LD for the 16 city gym guides
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'HYROX Gym Directories by City',
      itemListElement: HYROX_CITIES.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: `Best HYROX Training Gyms in ${c.city}`,
        url: `${SITE_URL}/blogs/article/${c.slug}`,
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'near-me-citylist-jsonld';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const stateCounts = useMemo(() => {
    const counts = {};
    ALL_GYMS.forEach((g) => { counts[g.state] = (counts[g.state] || 0) + 1; });
    return counts;
  }, []);

  const nearestGyms = useMemo(() => {
    if (!userLocation) return [];
    return ALL_GYMS
      .map((g) => {
        const coords = CITY_COORDS[`${g.city}, ${g.state}`];
        if (!coords) return null;
        return { ...g, distance: Math.round(getDistanceMiles(userLocation.lat, userLocation.lng, coords[0], coords[1])) };
      })
      .filter(Boolean)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 12);
  }, [userLocation]);

  const nearestCity = useMemo(() => (userLocation ? nearestCityLabel(userLocation) : null), [userLocation]);

  const nearbyCityGuides = useMemo(() => {
    if (!nearestGyms.length) return [];
    const nearStates = [...new Set(nearestGyms.slice(0, 6).map((g) => g.state))];
    return HYROX_CITIES.filter((c) => nearStates.includes(c.state)).slice(0, 3);
  }, [nearestGyms]);

  const stateGyms = useMemo(() => {
    if (!selectedState) return [];
    return ALL_GYMS.filter((g) => g.state === selectedState).sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
  }, [selectedState]);

  const handleLocate = useCallback(() => {
    if (!navigator.geolocation) { setGeoStatus('error'); return; }
    setGeoStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoStatus('success');
        trackGAEvent('near_me_locate', { page: '/hyrox/near-me', status: 'success' });
        setTimeout(() => document.getElementById('near-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      },
      (err) => {
        setGeoStatus(err.code === 1 ? 'denied' : 'error');
        trackGAEvent('near_me_locate', { page: '/hyrox/near-me', status: 'denied' });
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  return (
    <main className="pt-20 min-h-screen bg-[#050505]" data-testid="hyrox-near-me-page">
      <PageSEO
        title="HYROX Gyms Near Me — Find HYROX Training Near You | NinjAthlete"
        description={`Find HYROX training near you: ${ALL_GYMS.length} verified gyms across ${GYM_STATES.length} US states — Official HYROX Partner gyms, F45, BFT & ISI Elite clubs. Use your location or browse by city and state.`}
        path="/hyrox/near-me"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'HYROX Hub', url: '/hyrox' }, { name: 'HYROX Near Me', url: '/hyrox/near-me' }]}
      />
      <PageFAQSchema faqs={NEAR_ME_FAQS} pageName="HYROX Gyms Near Me — FAQs" pagePath="/hyrox/near-me" scriptId="near-me-faq-schema" />

      {/* Hero */}
      <section className="relative border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-6 py-16 sm:py-20">
          <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-4 flex items-center gap-2">
            <Crosshair className="w-3.5 h-3.5" /> Local Gym Finder
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-5">
            HYROX Gyms <span className="text-[#e8ff00]">Near Me</span>
          </h1>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl leading-relaxed mb-3">
            Find HYROX training near you in seconds. We track <strong className="text-white">{ALL_GYMS.length} HYROX-ready gyms across {GYM_STATES.length} US states</strong> — Official HYROX Partner gyms, F45 and BFT HYROX Training Clubs, ISI Elite partners, and vetted HYROX-aligned boxes.
          </p>
          <p className="text-zinc-500 text-sm max-w-2xl leading-relaxed mb-8">
            Tap the button below to rank every gym by distance from your location, or browse by state and city further down.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={handleLocate}
              disabled={geoStatus === 'loading'}
              className="inline-flex items-center gap-2.5 px-7 py-4 bg-[#e8ff00] text-black font-heading text-sm uppercase tracking-wider hover:opacity-90 disabled:opacity-60 transition-opacity"
              data-testid="near-me-locate-btn"
            >
              {geoStatus === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
              {geoStatus === 'loading' ? 'Locating…' : 'Use My Location'}
            </button>
            <a href="#browse-state" className="inline-flex items-center gap-2 px-6 py-4 border border-zinc-700 text-zinc-300 font-heading text-sm uppercase tracking-wider hover:border-[#e8ff00]/50 hover:text-[#e8ff00] transition-colors" data-testid="near-me-browse-state-link">
              Browse by State <ChevronDown className="w-4 h-4" />
            </a>
          </div>
          {(geoStatus === 'denied' || geoStatus === 'error') && (
            <p className="mt-4 text-sm text-[#E63946]" data-testid="near-me-geo-error">
              {geoStatus === 'denied'
                ? 'Location access was denied — no problem. Pick your state below to see every HYROX gym near you.'
                : "We couldn't get your location. Browse by state below instead."}
            </p>
          )}
        </div>
      </section>

      {/* Nearest results */}
      {geoStatus === 'success' && nearestGyms.length > 0 && (
        <section id="near-results" className="max-w-5xl mx-auto px-6 py-14" data-testid="near-me-results">
          <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-2">Your Results</p>
          <h2 className="font-heading text-lg text-white mb-1">
            {nearestGyms.length} HYROX gyms near you{nearestCity ? ` — closest metro: ${nearestCity}` : ''}
          </h2>
          <p className="text-zinc-500 text-sm mb-8">Distances are approximate (city-center based). Sorted nearest first.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {nearestGyms.map((g) => <NearGymCard key={`${g.name}-${g.city}`} gym={g} distance={g.distance} />)}
          </div>
          {nearbyCityGuides.length > 0 && (
            <div className="mt-8 bg-[#111] border border-[#e8ff00]/25 p-6" data-testid="near-me-city-guide-callout">
              <p className="font-mono text-[10px] tracking-[0.2em] text-[#e8ff00] uppercase mb-2">Deep-Dive Guides For Your Area</p>
              <div className="flex flex-wrap gap-3">
                {nearbyCityGuides.map((c) => (
                  <Link key={c.slug} to={`/blogs/article/${c.slug}`} className="inline-flex items-center gap-2 px-4 py-2.5 bg-zinc-900 border border-zinc-800 hover:border-[#e8ff00]/50 text-white text-sm font-heading transition-colors">
                    <BookOpen className="w-4 h-4 text-[#e8ff00]" /> Best HYROX Gyms in {c.city} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* City guides — our most thorough coverage, above the state browse */}
      <section className="max-w-5xl mx-auto px-6 py-14 border-t border-zinc-900">
        <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-2">City Guides</p>
        <h2 className="font-heading text-lg text-white mb-2">In-Depth HYROX Gym Directories by City</h2>
        <p className="text-zinc-500 text-sm mb-8 max-w-2xl">Our most thorough coverage — full profiles, videos, pricing, class schedules and race alerts for the biggest HYROX metros in the US:</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {HYROX_CITIES.map((c) => (
            <Link key={c.slug} to={`/blogs/article/${c.slug}`} className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#e8ff00]/40 p-4 transition-all" data-testid={`near-me-city-${c.city.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-3.5 h-3.5 text-[#e8ff00]" />
                <span className="text-white text-sm font-heading group-hover:text-[#e8ff00] transition-colors">{c.city}</span>
              </div>
              <span className="text-zinc-600 text-xs">{c.state}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Browse by state */}
      <section id="browse-state" className="max-w-5xl mx-auto px-6 py-14 border-t border-zinc-900">
        <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-2">Directory</p>
        <h2 className="font-heading text-lg text-white mb-6">Browse HYROX Gyms by State</h2>
        <div className="relative mb-3">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search any gym by name or city — covers all state listings and city directories..."
            className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
            data-testid="near-me-gym-search"
          />
        </div>
        {dirMatches.length > 0 && (
          <div className="mb-6 border border-[#e8ff00]/30 bg-[#e8ff00]/5 p-4" data-testid="near-me-dir-results">
            <p className="text-[#e8ff00] text-[10px] font-heading uppercase tracking-widest mb-2.5">
              Found in our City Gym Directories — full profiles, pricing &amp; schedules
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {dirMatches.map((g) => (
                <Link key={g.path} to={g.path} className="flex items-center justify-between gap-3 bg-zinc-900 border border-zinc-800 hover:border-[#e8ff00]/50 px-4 py-3 transition-colors" data-testid={`near-me-dir-match-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{g.title}</p>
                    <p className="text-zinc-500 text-xs truncate">{g.context}</p>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-[#e8ff00] flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2 mb-8">
          {GYM_STATES.map((st) => (
            <button
              key={st}
              onClick={() => setSelectedState(selectedState === st ? '' : st)}
              className={`px-3 py-2 text-xs font-heading uppercase tracking-wider border transition-colors ${
                selectedState === st ? 'bg-[#e8ff00] text-black border-[#e8ff00]' : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-[#e8ff00]/40 hover:text-[#e8ff00]'
              }`}
              data-testid={`near-me-state-${st}`}
            >
              {st} <span className={selectedState === st ? 'text-black/60' : 'text-zinc-600'}>({stateCounts[st] || 0})</span>
            </button>
          ))}
        </div>
        {selectedState && (
          <div data-testid="near-me-state-results">
            {HYROX_CITIES.some((c) => c.state === selectedState) && (
              <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="near-me-state-directories">
                <span className="text-zinc-500 text-xs uppercase tracking-wider font-heading">Deep-dive directories in {STATE_NAMES[selectedState]}:</span>
                {HYROX_CITIES.filter((c) => c.state === selectedState).map((c) => (
                  <Link key={c.slug} to={`/blogs/article/${c.slug}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#e8ff00]/10 border border-[#e8ff00]/40 text-[#e8ff00] text-xs font-heading uppercase tracking-wider hover:bg-[#e8ff00] hover:text-black transition-colors" data-testid={`near-me-state-dir-${c.city.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    {c.city} Directory <ArrowRight className="w-3 h-3" />
                  </Link>
                ))}
              </div>
            )}
            <h3 className="font-heading text-base text-white mb-4">
              {stateGyms.length} HYROX gym{stateGyms.length !== 1 ? 's' : ''} in {STATE_NAMES[selectedState]}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {stateGyms.map((g) => <NearGymCard key={`${g.name}-${g.city}`} gym={g} distance={userLocation ? Math.round(getDistanceMiles(userLocation.lat, userLocation.lng, ...(CITY_COORDS[`${g.city}, ${g.state}`] || [0, 0]))) : null} />)}
            </div>
          </div>
        )}
      </section>

      {/* How to choose */}
      <section className="max-w-5xl mx-auto px-6 py-14 border-t border-zinc-900">
        <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-2">Before You Join</p>
        <h2 className="font-heading text-lg text-white mb-6">How to Choose a HYROX Gym Near You</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            {[
              ['Official HYROX Partner / Training Club', 'Licensed by HYROX with race-spec sleds, SkiErgs, and wall ball targets. Best for race simulations and standardized programming.'],
              ['F45 & BFT HYROX Clubs', 'Global HYROX partners running HYROX Signature classes — great class energy and coaching, ideal for first-timers.'],
              ['ISI Elite Partners', 'Official training partners with monthly performance fitness test events — strong for benchmarking progress.'],
              ['HYROX-Aligned Boxes', 'CrossFit and functional gyms with the right equipment mix. Verify they have sleds and SkiErgs before joining.'],
            ].map(([title, desc]) => (
              <div key={title} className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#e8ff00] shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white text-sm font-heading mb-1">{title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-[#111] border border-zinc-800 p-6 h-fit">
            <p className="font-mono text-[10px] tracking-[0.2em] text-[#e8ff00] uppercase mb-3">The 4-Point Checklist</p>
            <ul className="space-y-2.5 text-sm text-zinc-400">
              <li className="flex gap-2"><span className="text-[#e8ff00] font-heading">01</span> Race-weight sleds + turf or sled track</li>
              <li className="flex gap-2"><span className="text-[#e8ff00] font-heading">02</span> SkiErg and rower availability at peak hours</li>
              <li className="flex gap-2"><span className="text-[#e8ff00] font-heading">03</span> Coaches who have raced HYROX themselves</li>
              <li className="flex gap-2"><span className="text-[#e8ff00] font-heading">04</span> Monthly race simulations or benchmark WODs</li>
            </ul>
            <Link to="/blogs/article/hyrox-beginner-guide" className="inline-flex items-center gap-2 mt-5 text-[#e8ff00] text-xs font-mono uppercase tracking-wider hover:gap-3 transition-all" data-testid="near-me-beginner-guide-link">
              Read the HYROX Beginner Guide <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Tools CTA band */}
      <section className="max-w-5xl mx-auto px-6 py-14 border-t border-zinc-900">
        <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-2">Train Smarter</p>
        <h2 className="font-heading text-lg text-white mb-6">Found Your Gym? Prep for Race Day</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { to: '/tools/hyrox-finish-time', icon: Timer, title: 'Finish Time Predictor', desc: 'Predict your race time and see where you rank vs. other athletes.', testid: 'near-me-cta-predictor' },
            { to: '/hyrox/benchmark-report', icon: BarChart3, title: 'HYROX Benchmark Report', desc: 'Average finish times & station splits by division — original data.', testid: 'near-me-cta-benchmark' },
            { to: '/blogs/article/hyrox-8-week-training-plan', icon: Dumbbell, title: '8-Week Training Plan', desc: 'The complete week-by-week plan to get race-ready from scratch.', testid: 'near-me-cta-plan' },
          ].map(({ to, icon: Icon, title, desc, testid }) => (
            <Link key={to} to={to} className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#e8ff00]/50 p-6 transition-all" data-testid={testid}>
              <Icon className="w-6 h-6 text-[#e8ff00] mb-3" />
              <h3 className="text-white font-heading text-base mb-1.5 group-hover:text-[#e8ff00] transition-colors">{title}</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">{desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-5xl mx-auto px-6 py-14 border-t border-zinc-900" data-testid="near-me-faq-section">
        <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-2">FAQ</p>
        <h2 className="font-heading text-lg text-white mb-6">HYROX Near Me — Common Questions</h2>
        <div className="space-y-3">
          {NEAR_ME_FAQS.map((f, i) => (
            <div key={f.question} className="bg-zinc-900/60 border border-zinc-800">
              <button
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 p-5 text-left"
                data-testid={`near-me-faq-${i}`}
              >
                <span className="text-white text-sm font-heading">{f.question}</span>
                <ChevronDown className={`w-4 h-4 text-[#e8ff00] shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === i && <p className="px-5 pb-5 text-zinc-400 text-sm leading-relaxed">{f.answer}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* Submit gym */}
      <section className="max-w-5xl mx-auto px-6 py-14 border-t border-zinc-900">
        <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-2">Gym Owners</p>
        <h2 className="font-heading text-lg text-white mb-2">Own a HYROX-Ready Gym? Get Listed Free</h2>
        <p className="text-zinc-500 text-sm mb-6 max-w-2xl">Join {ALL_GYMS.length}+ gyms in the most comprehensive HYROX directory in the US. Every submission is reviewed by our team.</p>
        <SubmitGymForm />
        <div className="mt-10 pt-8 border-t border-zinc-900 flex flex-wrap gap-x-6 gap-y-3 text-sm">
          <Link to="/hyrox" className="text-zinc-500 hover:text-[#e8ff00] transition-colors inline-flex items-center gap-1.5" data-testid="near-me-hub-link"><ArrowRight className="w-3.5 h-3.5" /> HYROX Training Hub</Link>
          <Link to="/hyrox/gyms" className="text-zinc-500 hover:text-[#e8ff00] transition-colors inline-flex items-center gap-1.5" data-testid="near-me-gyms-hub-link"><ArrowRight className="w-3.5 h-3.5" /> All City Directories</Link>
          <Link to="/gym-directory" className="text-zinc-500 hover:text-[#e8ff00] transition-colors inline-flex items-center gap-1.5" data-testid="near-me-directory-link"><ArrowRight className="w-3.5 h-3.5" /> Full Gym Directory (Map View)</Link>
          <Link to="/blogs/article/the-ultimate-guide-to-hyrox-training-plans-and-workouts-get-race-ready" className="text-zinc-500 hover:text-[#e8ff00] transition-colors inline-flex items-center gap-1.5" data-testid="near-me-pillar-link"><ArrowRight className="w-3.5 h-3.5" /> Ultimate HYROX Training Guide</Link>
        </div>
      </section>
    </main>
  );
}
